"use client";

import { openModal } from "@decibeltrade/web-core/modal";
import dynamic from "next/dynamic";
import { useTranslations } from "next-intl";
import { useCallback } from "react";

import { useAuthState } from "@/hooks/use-auth-state";

import { toast } from "../../toast";
import { LazyModalCloseProvider } from "../lazy-modal-close";
import { WithdrawModalSkeleton } from "./withdraw-modal-skeleton";
import { type WithdrawAssetValue } from "./withdraw-modal-state";

export type { WithdrawAssetValue, WithdrawDestination } from "./withdraw-modal-state";

interface WithdrawModalProps {
  subaccountAddress: string;
  /**
   * Which asset to open on. Callers name it in the modal's own vocabulary, so a row that knows it
   * holds APT can say so without a second set of token names to keep in step.
   */
  defaultAsset?: WithdrawAssetValue;
}

/**
 * Kept behind a dynamic import for the same reason as the deposit modal: this module is a door
 * into the cross-chain bridge stack (Wormhole, Solana web3, the Mesh SDK), which no route should
 * be paying for on first load.
 */
const loadWithdrawModal = () => import("./withdraw-modal");

const WithdrawModalContent = dynamic(
  () => loadWithdrawModal().then((m) => m.WithdrawModalContent),
  {
    ssr: false,
    loading: () => <WithdrawModalSkeleton />,
  },
);

/**
 * Starts the chunk downloading before the click that needs it — wire to hover/focus of anything
 * that opens the withdraw modal. Repeat calls are free: the import resolves from the module cache.
 */
export const preloadWithdrawModal = () => void loadWithdrawModal();

export const useOpenWithdrawModal = ({ subaccountAddress }: WithdrawModalProps) => {
  const tToast = useTranslations("toast");
  const { state } = useAuthState();
  return useCallback(
    (options?: { defaultAsset?: WithdrawAssetValue }) => {
      if (state === "SESSION_KEY_IMPORTED") {
        toast({
          variant: "error",
          title: tToast("titles.withdrawalsNotAvailable"),
          description: tToast("summaries.withdrawalsNotAvailableSessionKeyWarning"),
        });
        return;
      }
      openWithdrawModal({ subaccountAddress, defaultAsset: options?.defaultAsset });
    },
    [state, tToast, subaccountAddress],
  );
};

const openWithdrawModal = (args: WithdrawModalProps) => {
  openModal({
    id: "withdraw",
    renderContent: ({ close, isOpen }) => (
      <LazyModalCloseProvider value={close}>
        <WithdrawModalContent close={close} isOpen={isOpen} {...args} />
      </LazyModalCloseProvider>
    ),
  });
};
